import { getReference } from '../../../components/custom-worksheet/utils';
import { CWTRef, CWTSchema, CWTSchemaCalculation, CWTSchemaProperty, GeneratorFunction } from '../../../types/custom-worksheet-v2';
import { isRef } from '../../worksheet-v2/custom-worksheet-v2/store/utils';
import {
    isCWTSchemaField,
    isCWTSchemaList,
    isCWTSchemaSection,
    isCWTSchemaSectionGroup,
    isCWTSchemaTable,
    isInCollection,
} from '../store/utils';
import { FieldReferenceMap } from './types';
import yaml from 'js-yaml';
import traverse from 'traverse';

/**
 * Gets the fields from a CWT schema property.
 * @param property - the CWT schema property
 * @returns a map of field references
 */
function getFields(property: CWTSchemaProperty): FieldReferenceMap[] {
    let fields: FieldReferenceMap[] = [];
    if (isCWTSchemaField(property)) {
        fields = [...fields, { ref: property.id.split('.').pop() as string, field: property }];
    }
    if (isCWTSchemaSectionGroup(property)) {
        for (const key in property.properties) {
            const subProperty = property.properties[key];
            fields = [...fields, ...getFields(subProperty)];
        }
    }

    if (isCWTSchemaList(property)) {
        const item = property.properties.item;
        for (const key in item.properties) {
            const subProperty = item.properties[key];
            fields = [...fields, ...getFields(subProperty)];
        }
    }

    if (isCWTSchemaTable(property)) {
        const row = property.properties.row;
        for (const key in row.properties) {
            const field = row.properties[key];
            fields.push({ ref: key, field });
        }
        fields.push({ ref: property.id.split('.').pop() as string, field: property });
    }

    if (isCWTSchemaSection(property)) {
        for (const key in property.properties) {
            const field = property.properties[key];
            fields.push({ ref: key, field });
        }

        const sectionSummary = property.summary?.properties[getReference(property.id)];
        if (sectionSummary) {
            fields.push({ ref: getReference(property.id), field: sectionSummary });
        }
    }

    return fields;
}

/**
 * Maps the fields in a CWT schema to a reference map.
 * @param schema - the CWT schema
 * @returns a map of field references
 */
export function mapSchemaFieldsToReference(schema: CWTSchema): FieldReferenceMap[] {
    return Object.keys(schema.properties).reduce<FieldReferenceMap[]>((refs, key) => {
        const property = schema.properties[key];
        return [...refs, ...getFields(property)];
    }, []);
}

/**
 * Initializes the Monaco web worker.
 */
export function initMonacoWebworker(): void {
    window.MonacoEnvironment = {
        getWorker(moduleId, label): Worker {
            switch (label) {
                case 'editorWorkerService':
                    return new Worker(new URL('monaco-editor/esm/vs/editor/editor.worker', import.meta.url));
                case 'yaml':
                    return new Worker(new URL('monaco-yaml/yaml.worker', import.meta.url));
                default:
                    throw new Error(`Unknown label ${label}`);
            }
        },
    };
}

/**
 * Translates a calculation to YAML.
 * @param calc - the calculation to translate
 * @returns the YAML representation of the calculation
 */
export function calcToYaml(calc?: CWTSchemaCalculation | GeneratorFunction | null): string {
    if (!calc) {
        return '';
    }

    let calCopy: CWTSchemaCalculation | GeneratorFunction | string;

    if ('operation' in calc) {
        // @ts-ignore
        calCopy = structuredClone({ type: 'GENERATOR', return: { operation: calc.operation, params: calc.params } });
    } else {
        calCopy = structuredClone(calc);
    }

    if (isRef(calCopy)) {
        const collectionRef = calCopy.ref.match(/(__.+__)/gm);

        if (collectionRef === null) {
            calCopy = `$${calCopy.ref.split('.').at(-1)}`;
        } else {
            const match = collectionRef[0];
            // Add the collection accessor (__self__, __prev__, __next__) to the start of the reference
            calCopy = `${match}.$${calCopy.ref.split('.').at(-1)}`;
        }
    } else {
        traverse(calCopy).forEach(function (x) {
            if (isRef(x)) {
                const collectionRef = [...x.ref.matchAll(/(__(?:self|prev|next)__)/gm)]?.at(-1);

                if (!collectionRef || collectionRef.length === 0) {
                    x = `$${x.ref.split('.').at(-1)}`;
                } else {
                    const match = collectionRef.pop();

                    // Add the collection accessor (__self__, __prev__, __next__) to the start of the reference
                    x = `${match}.$${x.ref.split('.').at(-1)}`;
                }
                this.update(x);
            }
        });
    }

    return yaml.dump(calCopy, {
        // Remove line breaks
        lineWidth: Number.MAX_SAFE_INTEGER,
    });
}

/**
Translates a YAML representation of a calculation back to its original form.
* @param value - the YAML representation of the calculation
* @param references - a list of field references used in the calculation
* @returns the original form of the calculation
 */
export function yamlToCalc(
    currentField: string,
    schema: CWTSchema,
    value: string,
    references: FieldReferenceMap[]
): CWTSchemaCalculation | GeneratorFunction | null {
    let calc = yaml.load(value);

    if (!calc) {
        return null;
    }

    // Just a simple reference
    if (typeof calc === 'string') {
        calc = convertQuickRefToFull(currentField, schema, calc, references);
    }
    // Function or Expression or Condition or Aggregation
    else {
        traverse(calc).forEach(function (x) {
            if (typeof x === 'string') {
                this.update(convertQuickRefToFull(currentField, schema, x, references));
            }
        });

        // @ts-ignore
        if (calc.type === 'GENERATOR') {
            return {
                // @ts-ignore
                operation: calc.return.operation,
                // @ts-ignore
                params: calc.return.params,
            };
        }
    }

    return calc as CWTSchemaCalculation;
}

/**
 * Translates a quick reference to a full reference.
 * @param currentField - the current field we are writing a calculation for
 * @param quickRef - the quick reference to translate
 * @param references - a list of field references used in the schema
 * @returns the full reference or null if the quick reference cannot be translated or the original string if does not start with the correct syntax
 * @example $total -> {ref: #properties.total}
 */
function convertQuickRefToFull(
    currentField: string,
    schema: CWTSchema,
    quickRef: string,
    references: FieldReferenceMap[]
): CWTRef | string | null {
    // Matches any __self__, __prev__, __next__ syntax
    const collectionItemIndexVar = /__(?:self|prev|next)__\.\$.+/g;
    // Captures that syntax for later use
    const collectionItemIndexVarCapture = /(__(?:self|prev|next)__)\.\$.+/g;

    // The user is referencing a non-collection item
    if (quickRef.startsWith('$')) {
        const ref = quickRef.substring(1);
        const fieldRef = references.find((r) => r.ref === ref);

        if (!fieldRef) {
            return null;
        }

        // Check in collection
        const collection = isInCollection(schema, fieldRef.field);

        if (collection) {
            // We are actually referencing something in a collection
            if (isCWTSchemaTable(collection)) {
                // We are trying to reference a summary of a table
                // e.g $column1 would be the column1 property of the summary
                const summary = collection.summary;

                if (!summary) {
                    return null;
                }
                const prop = summary.properties[fieldRef.ref];

                if (!prop) {
                    return null;
                }
                // Just reference the id of the summarised column
                return { ref: `#${prop.id}` };
            }
            return null;
        }

        // Just reference the id of the summarised field
        return fieldRef ? { ref: `#${fieldRef.field.id}` } : null;
    } else if (collectionItemIndexVar.test(quickRef)) {
        const matches = quickRef.matchAll(collectionItemIndexVarCapture);

        const match = Array.from(matches)[0][1];

        // if the reference contains a __self__ or __prev__ or __next__
        if (match) {
            // Get the field reference from the match e.g. __self__.$column1 -> $column1
            const ref = quickRef.split('.').at(-1);
            const fieldRef = references.find((r) => r.ref === ref?.substring(1));
            if (!fieldRef) {
                return null;
            }

            // Check if the field is a collection item
            const collection = isInCollection(schema, fieldRef.field);
            if (!collection) {
                return null;
            }

            // We are actually referencing something in a collection
            if (isCWTSchemaTable(collection)) {
                // Check if the field we are configuring the calculation for also belongs in the same table
                const fieldCollection = isInCollection(schema, currentField);

                // If not, let's assume __self__ or __prev__ or __next__ refers to the current list item
                if (fieldCollection?.id !== collection.id) {
                    // Is this table in a list?
                    const list = isInCollection(schema, collection);
                    const summary = collection.summary;

                    if (!summary) {
                        return null;
                    }

                    // Find the summary of the referenced field
                    const prop = summary.properties[fieldRef.ref];

                    if (!prop) {
                        return null;
                    }

                    let ref = `#${prop.id}`;

                    // If we are in a list item, we need to replace item with the desired __self__ or __prev__ or __next__
                    if (list) {
                        ref = ref.replace('.item.', `.${match}.`);
                    }

                    return { ref };
                } else {
                    // Split the id into 2 parts
                    // The startId is the id of the collection
                    const startId = fieldRef.field.id
                        .substring(0, collection.id.length + 1)
                        .replace('.item.', `.__self__.`)
                        .replace('.row.', `.${match}.`);
                    // The itemId is the remaining id of the collection item
                    const itemId = fieldRef.field.id.substring(collection.id.length + 1).replace('.row.', `.${match}.`);

                    return fieldRef ? { ref: `#${startId}${itemId}` } : null;
                }
            }

            const startId = fieldRef.field.id.substring(0, collection.id.length + 1);
            // The itemId is the remaining id of the collection item
            const itemId = fieldRef.field.id.substring(collection.id.length + 1);

            // If we are in a collection item, we need to replace item/row with the desired __self__ or __prev__ or __next__
            return fieldRef ? { ref: `#${startId}${itemId.replace('.item.', `.${match}.`).replace('.row.', `.${match}.`)}` } : null;
        }
    }

    return quickRef;
}
